import * as ProDuotoneSvgIcons from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Section } from 'src/tailwind/components/Section'
import ErrorElement from 'src/views/components/ErrorElement'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import PrintButton from 'src/views/components/PrintButton'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

const REFERENCE_SECTION_ID = 'clinicalInternshipReferenceSection'

export default function AdministratorStudentClinicalInternshipReferencePage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('reference:clinical_internship')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  const { uid, profileId } = useParams()
  const [searchParams] = useSearchParams()
  const semId = searchParams.get('semId')

  const { data, isLoading, error } = useApi({
    endpoint: Api.getAdministratorStudentClinicalInternshipReference,
    params: React.useMemo(
      () => ({
        headers,
        args: { uid: uid!, profileId: profileId! },
        query: semId != null ? { semId } : undefined,
      }),
      [headers, uid, profileId, semId]
    ),
    shouldFetch: uid != null && profileId != null,
    suspense: false,
  })

  const breadcrumbsItems = React.useMemo(
    () => [
      {
        page: `${t('system:users')} - ${t('student:students')}`,
        path: `/${locale}/administrator/users/students`,
      },
      {
        page: t('reference:clinical_internship'),
        path: `/${locale}/administrator/users/students/${uid}/${profileId}/reference/clinical-internship`,
      },
    ],
    [locale, profileId, t, uid]
  )

  return (
    <Section
      id={REFERENCE_SECTION_ID}
      title={t('reference:clinical_internship')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProDuotoneSvgIcons.faFileCertificate} />}
      rightElement={
        data?.html != null ? (
          <div className="print-export col-auto flex w-full justify-end">
            <PrintButton />
          </div>
        ) : undefined
      }
    >
      <ReferenceContent html={data?.html ?? null} isLoading={isLoading} error={error as base.CommonError | null} />
    </Section>
  )
}

function ReferenceContent({
  html,
  isLoading,
  error,
}: {
  readonly html: string | null
  readonly isLoading: boolean
  readonly error: base.CommonError | null
}): JSX.Element {
  const t = useTranslatable()

  if (error != null) return <ErrorElement errorProp={error} />
  if (isLoading) return <Loader className="m-auto flex" />
  if (html == null) {
    return (
      <NoContent
        header={t('reference:not_available')}
        subHeader={t('reference:issued_to_graduates_only')}
        image={Warning}
        marginTop="5"
      />
    )
  }

  return (
    <div className="mt-4 flex min-h-[calc(100vh-16rem)] w-full overflow-x-auto print:block print:min-h-0 print:overflow-visible">
      <div className="reference-sheet m-auto w-full rounded-card bg-white p-8 print:max-w-none print:rounded-none print:p-0">
        <h2 className="mb-8 text-center text-headline font-semibold tracking-[0.3em]">{t('resume:certificate')}</h2>
        <div data-testid="clinicalInternshipReference" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  )
}
